import React, { ReactElement, useCallback, useMemo, useRef } from "react"
import cn from "../../../utils/classes"
import RowContainer from "./Layout/RowContainer"
import { Typography, typographyTypes } from "./Typography"
import Button from "./Button"
import { ClickAwayDiv } from "./ClickAwayDiv"
import Icon from "./Icon"

interface DropDownButtonProps {
  buttonLabel: string
  children: string | JSX.Element | JSX.Element[] | ReactElement
  isDisabled?: boolean
  open: boolean
  setOpen: (open: boolean) => void
  onClick?: () => void
  styleOverrides?: string
}

export const DropDownButton: React.FC<DropDownButtonProps> = ({
  buttonLabel,
  children,
  open,
  setOpen,
  isDisabled = false,
  onClick,
  styleOverrides,
}) => {
  const clickAwayRef = useRef<HTMLDivElement>(null)
  // TODO: Extract HA design system styles out of this comp?
  // Note: HA Design system style
  const className = useMemo(() => {
    const defaultStyles = [
      "absolute",
      "z-30",
      "top-14",
      "right-4",
      "rounded-lg",
      "bg-white",
      "border-1/4",
      "border-solid",
      "border-gray-200",
    ]
    return cn(defaultStyles, styleOverrides)
  }, [styleOverrides])

  const handleClick = useCallback(() => {
    setOpen(!open)
    onClick && onClick()
  }, [onClick, open, setOpen])

  const handleClickEvent = useCallback(
    (event: MouseEvent) => {
      if (
        clickAwayRef.current &&
        !clickAwayRef.current.contains(event.target as Node)
      ) {
        handleClick()
      }
    },
    [handleClick, clickAwayRef],
  )

  const labelWithIcon = (
    <RowContainer>
      <Typography
        variant={typographyTypes.NORMAL}
        text={buttonLabel}
        // Note: HA Design system style
        styleOverrides="text-white !text-[17px] !font-[500] leading-normal font-brandon-grotesque"
      />

      <Icon iconType="chevronDownWhite" />
    </RowContainer>
  )

  return (
    <div className="relative pr-4 my-2">
      <Button
        buttonLabel={labelWithIcon}
        onClick={handleClick}
        tabIndex={0}
        buttonSize="xl"
        textBold={true}
        type="purpleBrand"
        disabled={isDisabled}
      />
      {open && (
        <ClickAwayDiv
          containerClassName={className}
          handleClickOutside={handleClickEvent}
          ref={clickAwayRef}
        >
          {children}
        </ClickAwayDiv>
      )}
    </div>
  )
}
